import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { getGsap, hasFinePointer, isSmallScreen } from '@/lib/motion';
import { BENTO_WIPE_END, BENTO_WIPE_START } from './BentoWipe';
import { canAnimate, hslTripletToRgb, supportsWebGL2 } from './heroEnv';
import { HERO_SCREENS, readScreenAnchors, syncStillProjection, type HeroHandoff } from './heroLayout';
import type { HeroScene, RGB } from './heroScene';
import './hero.css';

// HeroBackdrop — everything BEHIND the hero's text (immersive redesign,
// 2026-09-24): a CSS gradient in the stop's own palette (always there: first
// paint, no WebGL, reduced motion), the WebGL liquid-light scene faded in on
// top of it once three.js has loaded, and a scrim that keeps the headline
// readable over both.
//
// three.js is fetched with a dynamic import only when the welcome stop is on
// screen, after the first paint. A visitor who lands straight on a studio
// stop (a completion email, /corrido) never downloads it; if they later open
// the welcome stop it loads then and stays, dimmed, for the other stops.

const DEFAULT_PRIMARY = '40 88% 64%';
const DEFAULT_ACCENT = '184 52% 56%';
// Let the DOM stills finish their entrance before the 3D twins take over:
// the last one lands at 350 + 4 x 90 + 1200 = 1910ms (hero.css), and a twin
// revealed under a still that is still scaling up showed a doubled rim.
const MIN_HANDOFF_MS = 2000;
// Matches the host's opacity transition below. Written as an arbitrary
// property there: `duration-[1400ms]` is ambiguous with tailwindcss-animate's
// duration utility, so Tailwind emitted nothing and the fade ran at 150ms.
const CANVAS_FADE_MS = 1400;

type GlState = 'off' | 'loading' | 'ready' | 'lost' | 'slow';

export function HeroBackdrop({
  immersive,
  primaryHsl,
  accentHsl,
  stageRef,
  screensRef,
  calmRef,
  onHandoffChange,
}: {
  /** The welcome stop: full scene with screens. Other stops: a dim field. */
  immersive: boolean;
  primaryHsl: string | null;
  accentHsl: string | null;
  stageRef: RefObject<HTMLElement>;
  screensRef: RefObject<HTMLDivElement>;
  /** The headline block: the field keeps a calm pocket behind it. */
  calmRef: RefObject<HTMLElement>;
  onHandoffChange: (state: HeroHandoff) => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<HeroScene | null>(null);
  const immersiveRef = useRef(immersive);
  immersiveRef.current = immersive;
  const [wantGl, setWantGl] = useState(false);
  const [gl, setGl] = useState<GlState>('off');
  const [sceneTick, setSceneTick] = useState(0);
  const [minDelayDone, setMinDelayDone] = useState(false);

  // Decide once the welcome stop is shown: WebGL only in a real browser that
  // allows motion and has WebGL2 (three 0.180 needs it).
  useEffect(() => {
    if (!immersive || wantGl) return;
    if (!canAnimate() || !supportsWebGL2()) return;
    setWantGl(true);
  }, [immersive, wantGl]);

  // Counted from when the welcome stop (and so its stills) first shows: a
  // visitor who arrives on a studio stop gets the stills' full entrance too.
  useEffect(() => {
    if (!immersive || minDelayDone) return undefined;
    const id = window.setTimeout(() => setMinDelayDone(true), MIN_HANDOFF_MS);
    return () => window.clearTimeout(id);
  }, [immersive, minDelayDone]);

  // The stills wear the 3D camera's projection from their first paint, so
  // the twins can replace them pixel for pixel (heroLayout). Re-synced on
  // every layout change that can move a still relative to the canvas. Set
  // up in a frame callback: this layout effect runs before React attaches
  // the stage's ref (our parent) and the stills' (a later sibling), and a
  // rAF queued here still runs before the first paint, with both attached.
  useLayoutEffect(() => {
    if (!immersive || typeof requestAnimationFrame !== 'function') return undefined;
    let alive = true;
    let teardown: (() => void) | null = null;
    const raf = requestAnimationFrame(() => {
      const stage = stageRef.current;
      const root = screensRef.current;
      const host = hostRef.current;
      if (!alive || !stage || !root || !host) return;
      const sync = () => syncStillProjection(root, host);
      sync();
      const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
      ro?.observe(stage);
      ro?.observe(root);
      if (calmRef.current) ro?.observe(calmRef.current);
      window.addEventListener('resize', sync);
      void (document.fonts?.ready ?? Promise.resolve()).then(() => { if (alive) sync(); });
      teardown = () => {
        ro?.disconnect();
        window.removeEventListener('resize', sync);
      };
    });
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      teardown?.();
    };
    // stageRef/screensRef/calmRef are stable ref objects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immersive]);

  // Load + create the scene after first paint; dispose on unmount.
  useEffect(() => {
    if (!wantGl) return undefined;
    let cancelled = false;
    let timer = 0;
    setGl('loading');
    const raf = requestAnimationFrame(() => {
      timer = window.setTimeout(() => {
        import('./heroScene')
          .then(({ createHeroScene }) => {
            const host = hostRef.current;
            const stage = stageRef.current;
            if (cancelled || !host || !stage) return;
            try {
              sceneRef.current = createHeroScene({
                host,
                touchTarget: stage,
                screens: HERO_SCREENS,
                quality: hasFinePointer() && !isSmallScreen() ? 'high' : 'low',
                getAnchors: () => (immersiveRef.current ? readScreenAnchors(screensRef.current, hostRef.current) : []),
                getCalm: () => calmUv(calmRef.current, hostRef.current),
                onReady: () => setGl('ready'),
                onLost: () => setGl('lost'),
                onRestored: () => setGl('ready'),
                onSlow: () => setGl('slow'),
              });
              setSceneTick((n) => n + 1);
            } catch {
              setGl('off'); // no context after all: the CSS backdrop stays
            }
          })
          .catch(() => setGl('off'));
      }, 60);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
      sceneRef.current?.dispose();
      sceneRef.current = null;
    };
    // stageRef/screensRef/calmRef are stable ref objects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantGl]);

  // Run only while the stage is on screen, the tab is visible and no pick
  // cover (BentoWipe) hides it: under that opaque layer the shader and the
  // clips were rendering for nobody while React built the new stop.
  useEffect(() => {
    const scene = sceneRef.current;
    const host = hostRef.current;
    if (!scene || !host) return undefined;
    let onScreen = true;
    let wiping = typeof document !== 'undefined' && !!document.querySelector('[data-bento-wipe]');
    const apply = () => scene.setRunning(onScreen && !document.hidden && !wiping);
    const onWipeStart = () => { wiping = true; apply(); };
    const onWipeEnd = () => { wiping = false; apply(); };
    const io = typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(([entry]) => { onScreen = !!entry?.isIntersecting; apply(); }, { rootMargin: '80px 0px' })
      : null;
    io?.observe(host);
    document.addEventListener('visibilitychange', apply);
    window.addEventListener(BENTO_WIPE_START, onWipeStart);
    window.addEventListener(BENTO_WIPE_END, onWipeEnd);
    apply();
    return () => {
      io?.disconnect();
      document.removeEventListener('visibilitychange', apply);
      window.removeEventListener(BENTO_WIPE_START, onWipeStart);
      window.removeEventListener(BENTO_WIPE_END, onWipeEnd);
      scene.setRunning(false);
    };
  }, [sceneTick]);

  // Scroll: the fly-through is driven by the stage's own scroll progress.
  useEffect(() => {
    const scene = sceneRef.current;
    const stage = stageRef.current;
    if (!scene || !stage) return undefined;
    const { ScrollTrigger } = getGsap();
    ScrollTrigger.config({ ignoreMobileResize: true });
    const st = ScrollTrigger.create({
      trigger: stage,
      start: 'top top',
      end: 'bottom top',
      onUpdate: (self) => scene.setScroll(self.progress),
    });
    scene.setScroll(st.progress);
    return () => st.kill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneTick]);

  // Layout: re-read the stills whenever the stage or the band changes size,
  // the fonts land (the headline reflows and moves the band), or the stop
  // changes (the stills mount/unmount).
  useEffect(() => {
    const scene = sceneRef.current;
    const stage = stageRef.current;
    if (!scene || !stage) return undefined;
    scene.setImmersive(immersive);
    let frame = 0;
    // The stage changing height moves every ScrollTrigger below it, so they
    // are refreshed — but only once the page has stopped scrolling: a refresh
    // restores the scroll position, which cut short the smooth scroll a tool
    // card starts (back up to the compass) and left the page mid-way.
    let refreshTimer = 0;
    let lastScroll = 0;
    const onScroll = () => { lastScroll = performance.now(); };
    window.addEventListener('scroll', onScroll, { passive: true });
    const refreshWhenSettled = () => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(function settle() {
        if (performance.now() - lastScroll < 250) {
          refreshTimer = window.setTimeout(settle, 250);
          return;
        }
        getGsap().ScrollTrigger.refresh();
      }, 300);
    };
    const relayout = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        scene.relayout();
        refreshWhenSettled();
      });
    };
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(relayout) : null;
    ro?.observe(stage);
    if (screensRef.current) ro?.observe(screensRef.current);
    if (calmRef.current) ro?.observe(calmRef.current);
    // A phone's address bar showing or hiding fires 'resize' with only the
    // height changed. The stage is sized in svh, so nothing moved: skip it,
    // or every toolbar flick re-ran ScrollTrigger.refresh() and undid
    // ignoreMobileResize. Real layout changes still reach the observer above.
    let lastWidth = window.innerWidth;
    const onResize = () => {
      const w = window.innerWidth;
      if (w === lastWidth && !hasFinePointer()) return;
      lastWidth = w;
      relayout();
    };
    window.addEventListener('resize', onResize);
    let alive = true;
    void (document.fonts?.ready ?? Promise.resolve()).then(() => { if (alive) relayout(); });
    relayout();
    return () => {
      alive = false;
      cancelAnimationFrame(frame);
      window.clearTimeout(refreshTimer);
      ro?.disconnect();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneTick, immersive]);

  // Colours follow the stop (corrido amber/teal, TikTok cyan/pink, ...).
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    const a: RGB = hslTripletToRgb(primaryHsl ?? DEFAULT_PRIMARY);
    const b: RGB = hslTripletToRgb(accentHsl ?? DEFAULT_ACCENT);
    scene.setColors(a, b);
  }, [sceneTick, primaryHsl, accentHsl]);

  // Too slow for this device: drop the scene for good (the CSS backdrop and
  // the DOM stills are already underneath).
  useEffect(() => {
    if (gl !== 'slow' || !sceneRef.current) return;
    sceneRef.current.dispose();
    sceneRef.current = null;
    setSceneTick((n) => n + 1); // detach the observers and the ScrollTrigger
  }, [gl]);

  // Handoff order: the canvas (the light field only) fades in UNDER the DOM
  // stills first, holding still; then the twins appear beneath them at the
  // same pose and the stills fade out on top (heroScene setHandoff, hero.css).
  // Crossfading both at once washed the screens out for a moment.
  const [canvasIn, setCanvasIn] = useState(false);
  useEffect(() => {
    if (gl !== 'ready') {
      setCanvasIn(false);
      return undefined;
    }
    const id = window.setTimeout(() => setCanvasIn(true), CANVAS_FADE_MS);
    return () => window.clearTimeout(id);
  }, [gl]);

  // PHONES HAND OFF ON THE FIRST SCROLL (round 5b, 2026-09-25). The twins
  // render at the phone DPR cap (1.5), so on a 3x Android they are clearly
  // softer than the DOM stills, and a timed swap took the hero from sharp to
  // soft 3-6s after arrival with nothing moving. On a touch screen or a
  // small window the sharp stills now stay over the lit field ('hold', which
  // keeps them static) until the page has scrolled, and the twins take over
  // as the fly-through starts moving them, where the motion hides the lower
  // resolution. A real scroll only: a tap's jitter fires touchmove without
  // moving anything. One way: the twins never hand back. Desktop (the
  // scene's 'high' quality, DPR up to 2) keeps the timed handoff.
  const [timedHandoff] = useState(() => hasFinePointer() && !isSmallScreen());
  const [moved, setMoved] = useState(false);
  useEffect(() => {
    if (!immersive || timedHandoff || moved || typeof window === 'undefined') return undefined;
    const check = () => { if (window.scrollY > 8) setMoved(true); };
    check(); // a reload restored mid-page
    window.addEventListener('scroll', check, { passive: true });
    return () => window.removeEventListener('scroll', check);
  }, [immersive, timedHandoff, moved]);

  const handoff: HeroHandoff = canvasIn && minDelayDone && (timedHandoff || moved)
    ? 'on'
    : gl === 'loading' || gl === 'ready' ? 'hold' : 'off';
  useEffect(() => { onHandoffChange(handoff); }, [handoff, onHandoffChange]);
  useEffect(() => { sceneRef.current?.setHandoff(handoff === 'on'); }, [handoff, sceneTick]);

  return (
    <div
      aria-hidden="true"
      className="hero-backdrop pointer-events-none absolute inset-x-0 top-0 -z-10 overflow-hidden"
      data-gl={gl}
      data-testid="hero-backdrop"
    >
      <div className="hero-fallback absolute inset-0" />
      <div
        ref={hostRef}
        className="absolute inset-0 transition-opacity ease-expo-out [transition-duration:1400ms]"
        style={{ opacity: gl === 'ready' ? (immersive ? 1 : 0.85) : 0 }}
      />
      <div className={`hero-scrim absolute inset-0${immersive ? '' : ' hero-scrim--compact'}`} />
    </div>
  );
}

/** Centre of the headline block in the canvas's uv space (y up). */
function calmUv(block: HTMLElement | null, host: HTMLElement | null): [number, number] {
  if (!block || !host) return [0.5, 0.55];
  const h = host.getBoundingClientRect();
  const b = block.getBoundingClientRect();
  if (!h.width || !h.height) return [0.5, 0.55];
  const x = (b.left + b.width / 2 - h.left) / h.width;
  const y = 1 - (b.top + b.height / 2 - h.top) / h.height;
  return [Math.min(1, Math.max(0, x)), Math.min(1, Math.max(0, y))];
}
